import React, { useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "emailjs-com";

export function About() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");

  const focusAreas = [
    "Workflow-heavy product surfaces",
    "Pragmatic backend modernization",
    "Clear specs and technical writing",
    "Small decisions that compound",
  ];

  const productPrinciples = [
    {
      label: "Start with the workflow",
      description:
        "I care about what the user is trying to get done, where the friction sits, and which parts of the system need to become simpler.",
    },
    {
      label: "Make tradeoffs explicit",
      description:
        "Good product engineering is often choosing the boring, durable path and explaining why it is the right one for the moment.",
    },
    {
      label: "Keep the system legible",
      description:
        "The best implementation is one another engineer can safely change after the deadline, the launch, and the handoff.",
    },
  ];

  const socialLinks = [
    {
      label: "LinkedIn",
      href: "https://www.linkedin.com/in/jacob-marthaller/",
    },
    {
      label: "GitHub",
      href: "https://github.com/jmarthaller",
    },
    {
      label: "X",
      href: "https://x.com/marthallerj",
    },
    {
      label: "Medium",
      href: "https://marthaller-jr.medium.com/",
    },
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    const templateParams = {
      name: name,
      email: email,
      message: message,
    };
    try {
      await emailjs.send(
        "service_emeb2vk",
        "contact_form",
        templateParams,
        "uoQxKowOftgv12YS0"
      );
      setStatus("Message sent successfully!");
    } catch (error) {
      setStatus("An error occurred. Please try again.");
    } finally {
      e.target.reset();
      setName("");
      setEmail("");
      setMessage("");
    }
  }

  return (
    <div id="about" className="home-page">
      <section className="home-scene" aria-labelledby="home-heading">
        <picture className="home-artwork" aria-hidden="true">
          <source
            media="(max-width: 767px)"
            srcSet="/images/human-horizon-tall.jpg"
            width="1120"
            height="1400"
          />
          <img
            src="/images/human-horizon-wide.jpg"
            alt=""
            width="1584"
            height="990"
          />
        </picture>

        <div className="home-introduction">
          <p className="home-introduction__lede">
            I'm Jacob Marthaller, a software engineer working primarily in C#,
            TypeScript, and Ruby. I like the part of engineering where product
            judgment, system design, and clear communication all have to meet
            in the same place.
          </p>
          <div className="home-introduction__links">
            <Link to="/blog">
              Read the blog
            </Link>
            <a href="#contact">
              Reach out
            </a>
          </div>
        </div>
      </section>

      <section className="home-field-notes" aria-labelledby="focus-heading">
        <div className="home-field-notes__grid">
          <div>
            <h2 id="focus-heading" className="field-notes__heading">
              Product-focused engineering
            </h2>
            <ul className="focus-list">
              {focusAreas.map((area) => (
                <li key={area}>{area}</li>
              ))}
            </ul>
          </div>

          <div className="field-notes__writing">
            <p className="section-kicker">Writing</p>
            <p>
              I've written about all kinds of things, including{" "}
              <a href="https://www.tandfonline.com/doi/full/10.1179/1462317X15Z.000000000146">
                open source software
              </a>
              ,{" "}
              <a href="https://search.lib.virginia.edu/sources/uva_library/items/2514nk71t">
                ethics
              </a>
              , and{" "}
              <a href="https://www.washingtonpost.com/news/acts-of-faith/wp/2017/03/23/trumps-new-drone-policy-might-violate-centuries-of-christian-ethics/">
                military drones
              </a>
              . This site is becoming the place where I write about software
              engineering and the ideas I keep returning to.
            </p>
            <div className="social-links" aria-label="Social profiles">
              {socialLinks.map((link) => (
                <a key={link.label} href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="home-principles" aria-labelledby="principles-heading">
        <div className="home-principles__inner">
          <div className="home-principles__intro">
            <p className="section-kicker">How I work</p>
            <h2 id="principles-heading">
              Product context first, implementation second.
            </h2>
            <p>
              The engineering work I enjoy most is close to the shape of the
              product: understanding the user path, trimming accidental
              complexity, and shipping changes that make the next decision
              easier instead of harder.
            </p>
          </div>

          <div className="principles-list">
            {productPrinciples.map((principle) => (
              <article key={principle.label} className="principle">
                <h3>{principle.label}</h3>
                <p>{principle.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home-contact" aria-labelledby="contact-heading">
        <form
          id="contact"
          name="contact"
          onSubmit={handleSubmit}
          className="contact-panel"
        >
          <div className="contact-panel__intro">
            <p className="section-kicker">Contact</p>
            <h2 id="contact-heading">Get in touch</h2>
            <p>
              Send a note about engineering work, writing, or a problem that
              needs sharper software.
            </p>
          </div>

          <div>
            {status && (
              <p className="contact-status" aria-live="polite">
                {status}
              </p>
            )}
            <div className="contact-fields">
              <div className="contact-field">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="contact-field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="contact-field contact-field--message">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
            </div>
            <button type="submit" className="contact-submit">
              Send message
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
